'use client';

/**
 * Feature Flag Context
 *
 * Provides feature flag state to the component tree via React Context,
 * backed by the /api/feature-flags/evaluated endpoint through React Query.
 * Flags refresh on window focus and every 60s stale time.
 *
 * Usage:
 *   const { isEnabled } = useFeatureFlag();
 *   if (isEnabled('my_feature')) { ... }
 */

import { createContext, useContext, ReactNode } from 'react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

interface FeatureFlagContextType {
  flags: Record<string, boolean>;
  isLoading: boolean;
  isEnabled: (key: string) => boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const value = useFeatureFlags();

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlag(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlag must be used within FeatureFlagProvider');
  }
  return context;
}
